import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  Label,
  PageSection,
  Spinner,
  Title
} from '@patternfly/react-core';
import { ArrowRightIcon, CommentIcon, OutlinedCommentsIcon } from '@patternfly/react-icons';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { SurveyApi } from '@app/api';
import { DataLoadError } from '@app/components/DataLoadError';
import { TranslatableText } from '@app/components/TranslatableText';
import { Klass } from '@app/model/Klass';
import { SURVEY_COMMENT_GROUPS, SurveyView } from '@app/model/Survey';
import { SurveyScores } from '@app/Classes/SurveyParts';
import { ApiError, isNotFound, toApiError } from '@app/utils/ApiError';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';

export interface ClassSurveyOverviewProps {
  /** The class the survey is about. Read for its own id and its enrolment count. */
  klass: Klass;
  /** Where the individual submissions live. Offered to an administrator only - see below. */
  surveyPath: string;
}

/**
 * The class's survey as everybody signed in may read it: the six scores, and the comments.
 *
 * This is the whole of what the service serves without `admin`, which is what makes it the tab
 * rather than a page behind a role. The comments arrive as five lists of strings with no name, no
 * email and no order that lines one list up with another, so a reader learns what was said about
 * the class and not who said it.
 *
 * That is not the same as the text being anonymous. A student who signs a comment, or names a
 * colleague in one, has published that to every user of this service, and nothing on this tab can
 * undo it.
 *
 * **It never asks for a submission.** The individual responses are identified data and live on
 * their own page - see ClassSurveyPage - so this tab makes exactly one request whoever is reading
 * it, and a reader without the role is not offered a link that would answer 403.
 */
export const ClassSurveyOverview: React.FunctionComponent<ClassSurveyOverviewProps> = ({
  klass,
  surveyPath
}) => {
  const navigate = useNavigate();
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  const [survey, setSurvey] = React.useState<SurveyView | undefined>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<ApiError | undefined>();
  /** No survey has been imported for this class, which is not a failure. */
  const [missing, setMissing] = React.useState<boolean>(false);

  const load = React.useCallback(async () => {
    if (!klass.id) return;
    setLoading(true);
    setError(undefined);
    setMissing(false);
    try {
      setSurvey(await SurveyApi.getByKlass(klass.id));
    } catch (loadError: unknown) {
      // Most classes have no survey, and the service does not distinguish "no survey" from "no
      // such class" - either way there is nothing to show, and neither is worth an error state.
      if (isNotFound(loadError)) setMissing(true);
      else setError(toApiError(loadError));
    } finally {
      setLoading(false);
    }
  }, [klass.id]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <PageSection hasBodyWrapper>
        <Bullseye><Spinner aria-label="Loading the survey" /></Bullseye>
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection hasBodyWrapper>
        <DataLoadError error={error} entityLabel="this class's survey" onRetry={load} />
      </PageSection>
    );
  }

  if (missing || !survey) {
    return (
      <PageSection hasBodyWrapper>
        <EmptyState headingLevel="h4" icon={OutlinedCommentsIcon} titleText="No survey for this class">
          <EmptyStateBody>
            Surveys are imported from the survey platform after a class is delivered, so a class
            that has just run will not have one yet. Nothing here can create one.
          </EmptyStateBody>
        </EmptyState>
      </PageSection>
    );
  }

  return (
    <PageSection hasBodyWrapper>
      <SurveyScores survey={survey} enrolled={klass.totalEnrolled} />

      {isAdmin && survey.nrSubmissions > 0 && (
        <div className="cas-survey-drilldown">
          <Button
            variant="secondary"
            icon={<ArrowRightIcon />}
            iconPosition="end"
            onClick={() => navigate(surveyPath)}
          >
            Open the individual submissions
          </Button>
          <span className="cas-survey-muted">
            Every answer by question and by respondent, and who wrote which comment.
            Administrators only.
          </span>
        </div>
      )}

      <SurveyCommentGroups survey={survey} />
    </PageSection>
  );
};

/**
 * The five comment boxes, each as what was written in it.
 *
 * The anonymised lists the service serves to everybody, not the comments read back off the
 * submissions. Which is why they are here and not on the survey page: this is the half of a survey
 * that is everybody's, and an administrator who wants a comment attributed has the Respondents tab
 * over there.
 */
export const SurveyCommentGroups: React.FunctionComponent<{ survey: SurveyView }> = ({ survey }) => {
  const groups = SURVEY_COMMENT_GROUPS.map(group => ({
    ...group,
    comments: survey.comments?.[group.group] ?? []
  })).filter(group => group.comments.length > 0);

  if (groups.length === 0) {
    return (
      <EmptyState headingLevel="h4" icon={CommentIcon} titleText="Nothing was written">
        <EmptyStateBody>
          Every comment box on every submission for this class was left empty.
        </EmptyStateBody>
      </EmptyState>
    );
  }

  return (
    <div className="cas-survey-comments">
      {groups.map(({ group, label, comments }) => (
        <section key={group}>
          <Title headingLevel="h3" size="md">
            {label} <Label isCompact>{comments.length}</Label>
          </Title>
          {comments.map((comment, index) => (
            // Indexed keys: the list is anonymous by design, so there is no identifier to key on,
            // and two students writing the same sentence is not impossible.
            <blockquote key={`${group}-${index}`} className="cas-survey-quote">
              {/* Both versions arrive on the comment; the switch is offered only where the two
                  differ. Translating a class is the survey page's action, not this tab's - this
                  tab is read by everybody and the translation is nobody's to trigger from here. */}
              <TranslatableText
                comment={comment}
                what={`${label.toLowerCase()} comment ${index + 1}`}
              />
            </blockquote>
          ))}
        </section>
      ))}
    </div>
  );
};
